(() => {
  'use strict';

  const STORAGE_KEY = 'flyandbag:wishlist:v1';
  const TOGGLE_SELECTOR = '[data-flyandbag-wishlist-toggle]';
  const TECHNICAL_PRODUCT_HANDLE = 'frais-service-pickup';
  let memoryItems = [];
  let scanQueued = false;

  const heartSvg = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path class="flyandbag-wishlist-heart-shape" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"></path>
    </svg>
  `;

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function readItems() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      memoryItems = Array.isArray(parsed) ? parsed.filter((item) => item && item.handle) : [];
    } catch (error) {
      console.warn('[FlyAndBag Wishlist] Lecture impossible.', error);
    }
    return [...memoryItems];
  }

  function writeItems(items) {
    memoryItems = Array.isArray(items) ? items : [];
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryItems));
    } catch (error) {
      console.warn('[FlyAndBag Wishlist] Enregistrement impossible.', error);
    }

    document.dispatchEvent(
      new CustomEvent('flyandbag:wishlist-change', {
        detail: { items: [...memoryItems], count: memoryItems.length },
      })
    );
  }

  function getProductHandle(url) {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const match = pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_error) {
      return '';
    }
  }

  function formatPrice(cents) {
    if (!Number.isFinite(Number(cents))) return '';
    const locale = document.documentElement.lang || 'fr-FR';
    const currency = window.Shopify?.currency?.active || 'EUR';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(Number(cents) / 100);
    } catch (_error) {
      return `${(Number(cents) / 100).toFixed(2)} €`;
    }
  }

  function itemFromButton(button) {
    return {
      handle: button.dataset.productHandle || '',
      title: button.dataset.productTitle || '',
      url: button.dataset.productUrl || `/products/${button.dataset.productHandle || ''}`,
      image: button.dataset.productImage || '',
      price: button.dataset.productPrice || '',
      available: button.dataset.productAvailable !== 'false',
    };
  }

  function isSaved(handle, items = readItems()) {
    return items.some((item) => item.handle === handle);
  }

  function setButtonState(button, active) {
    if (!(button instanceof HTMLElement)) return;

    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');

    const label = button.querySelector('[data-flyandbag-wishlist-label]');
    if (label) {
      label.textContent = active ? 'Dans ma wishlist' : 'Ajouter à ma wishlist';
    }

    const accessibleLabel = active ? 'Retirer de la wishlist' : 'Ajouter à la wishlist';
    button.setAttribute('aria-label', accessibleLabel);
    button.setAttribute('title', accessibleLabel);
  }

  function refreshButtons() {
    const items = readItems();
    document.querySelectorAll(TOGGLE_SELECTOR).forEach((button) => {
      setButtonState(button, isSaved(button.dataset.productHandle || '', items));
    });
  }

  function updateWishlistLinks() {
    const count = readItems().length;

    document.querySelectorAll('a[href*="/pages/wishlist"]').forEach((link) => {
      let badge = link.querySelector('[data-flyandbag-wishlist-count]');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'flyandbag-wishlist-menu-count';
        badge.dataset.flyandbagWishlistCount = '';
        badge.setAttribute('aria-label', `${count} article${count > 1 ? 's' : ''} dans la wishlist`);
        link.appendChild(badge);
      }

      badge.textContent = String(count);
      badge.hidden = count === 0;
      badge.setAttribute('aria-label', `${count} article${count > 1 ? 's' : ''} dans la wishlist`);
    });
  }

  function createToggle(item, mode) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `flyandbag-wishlist-toggle flyandbag-wishlist-toggle--${mode}`;
    button.dataset.flyandbagWishlistToggle = '';
    button.dataset.productHandle = item.handle;
    button.dataset.productTitle = item.title || '';
    button.dataset.productUrl = item.url || `/products/${item.handle}`;
    button.dataset.productImage = item.image || '';
    button.dataset.productPrice = item.price || '';
    button.dataset.productAvailable = item.available === false ? 'false' : 'true';
    button.innerHTML =
      mode === 'product'
        ? `${heartSvg}<span data-flyandbag-wishlist-label>Ajouter à ma wishlist</span>`
        : `${heartSvg}<span class="visually-hidden">Ajouter à la wishlist</span>`;

    setButtonState(button, isSaved(item.handle));
    return button;
  }

  function extractCardItem(card, link) {
    const handle = getProductHandle(link.href);
    const image = card.querySelector('img');
    const titleElement = card.querySelector(
      '.resource-card__title, .product-card__title, h2, h3, [class*="title"]'
    );
    const priceElement = card.querySelector('product-price, .price, [class*="price"]');

    return {
      handle,
      title: cleanText(titleElement?.textContent) || cleanText(image?.alt),
      url: link.getAttribute('href') || `/products/${handle}`,
      image: image?.currentSrc || image?.src || '',
      price: cleanText(priceElement?.textContent),
      available: !card.querySelector(
        '.color-custom-badge-sold-out, [class*="sold-out"], [class*="sold_out"]'
      ),
    };
  }

  function injectCardButtons(root = document) {
    const selector = '.resource-card[data-resource-type="product"], .product-card';
    const cards = [];

    if (root instanceof Element && root.matches(selector)) cards.push(root);
    if (root.querySelectorAll) cards.push(...root.querySelectorAll(selector));

    cards.forEach((card) => {
      if (card.querySelector('.flyandbag-wishlist-toggle--card')) return;

      const link = card.querySelector('a[href*="/products/"]');
      if (!link) return;

      const item = extractCardItem(card, link);
      if (!item.handle || item.handle === TECHNICAL_PRODUCT_HANDLE) return;

      const media =
        card.querySelector(
          '.resource-card__media, .product-card__media, .product-card__image, card-gallery, .card-gallery'
        ) || card;

      media.classList.add('flyandbag-wishlist-media-host');
      media.appendChild(createToggle(item, 'card'));
    });
  }

  function injectProductButton() {
    const handle = getProductHandle(window.location.href);
    if (!handle || handle === TECHNICAL_PRODUCT_HANDLE) return;
    if (document.querySelector('.flyandbag-wishlist-toggle--product')) return;

    const details = document.querySelector('.product-details');
    const heading = details?.querySelector('h1');
    if (!details || !heading) return;

    const image = document.querySelector('.product-information__media img, main img');
    const price = document.querySelector('.product-details product-price, .product-details .price');

    const item = {
      handle,
      title: cleanText(heading.textContent),
      url: window.location.pathname,
      image: image?.currentSrc || image?.src || '',
      price: cleanText(price?.textContent),
      available: !document.querySelector(
        '.product-details [disabled][name="add"], .product-details .color-custom-badge-sold-out'
      ),
    };

    const button = createToggle(item, 'product');
    const titleBlock =
      heading.closest('.text-block, [class*="text-block"], [data-block-id]') || heading.parentElement;

    if (titleBlock?.parentElement) {
      titleBlock.insertAdjacentElement('afterend', button);
    } else {
      details.prepend(button);
    }
  }

  async function enrichItem(item) {
    if (!item?.handle) return item;

    try {
      const response = await fetch(`/products/${encodeURIComponent(item.handle)}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return item;

      const product = await response.json();
      return {
        ...item,
        title: product.title || item.title,
        url: product.url || item.url || `/products/${item.handle}`,
        image: product.featured_image || item.image,
        price: formatPrice(product.price) || item.price,
        available: product.available !== false,
      };
    } catch (_error) {
      return item;
    }
  }

  function showToast(message) {
    let toast = document.querySelector('[data-flyandbag-wishlist-toast]');

    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'flyandbag-wishlist-toast';
      toast.dataset.flyandbagWishlistToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add('is-visible');

    window.clearTimeout(Number(toast.dataset.timeoutId || 0));
    const timeoutId = window.setTimeout(() => toast.classList.remove('is-visible'), 2200);
    toast.dataset.timeoutId = String(timeoutId);
  }

  async function toggleItem(button) {
    const item = itemFromButton(button);
    if (!item.handle) return;

    const items = readItems();
    const index = items.findIndex((savedItem) => savedItem.handle === item.handle);

    if (index >= 0) {
      items.splice(index, 1);
      writeItems(items);
      showToast('Article retiré de votre wishlist');
    } else {
      items.unshift(item);
      writeItems(items);
      showToast('Article ajouté à votre wishlist');

      const enriched = await enrichItem(item);
      const latestItems = readItems();
      const latestIndex = latestItems.findIndex((savedItem) => savedItem.handle === item.handle);
      if (latestIndex >= 0) {
        latestItems[latestIndex] = enriched;
        writeItems(latestItems);
      }
    }

    refreshButtons();
    updateWishlistLinks();
    renderWishlistPage();
  }

  function makeWishlistCard(item) {
    const card = document.createElement('article');
    card.className = 'flyandbag-wishlist-card';

    const mediaLink = document.createElement('a');
    mediaLink.className = 'flyandbag-wishlist-card__media';
    mediaLink.href = item.url || `/products/${item.handle}`;
    mediaLink.setAttribute('aria-label', `Voir ${item.title || 'le produit'}`);

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      mediaLink.appendChild(image);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'flyandbag-wishlist-card__placeholder';
      placeholder.innerHTML = heartSvg;
      mediaLink.appendChild(placeholder);
    }

    const body = document.createElement('div');
    body.className = 'flyandbag-wishlist-card__body';

    const title = document.createElement('h2');
    const titleLink = document.createElement('a');
    titleLink.href = item.url || `/products/${item.handle}`;
    titleLink.textContent = item.title || 'Produit FlyAndBag';
    title.appendChild(titleLink);

    const price = document.createElement('p');
    price.className = 'flyandbag-wishlist-card__price';
    price.textContent = item.price || '';

    if (item.available === false) {
      const status = document.createElement('p');
      status.className = 'flyandbag-wishlist-card__status';
      status.textContent = 'Actuellement épuisé';
      body.append(title, price, status);
    } else {
      body.append(title, price);
    }

    const actions = document.createElement('div');
    actions.className = 'flyandbag-wishlist-card__actions';

    const view = document.createElement('a');
    view.className = 'button flyandbag-wishlist-card__view';
    view.href = item.url || `/products/${item.handle}`;
    view.textContent = 'Voir le produit';

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'flyandbag-wishlist-card__remove';
    remove.dataset.flyandbagWishlistRemove = item.handle;
    remove.setAttribute('aria-label', `Retirer ${item.title || 'ce produit'} de la wishlist`);
    remove.innerHTML = `${heartSvg}<span>Retirer</span>`;

    actions.append(view, remove);
    body.appendChild(actions);
    card.append(mediaLink, body);

    return card;
  }

  async function renderWishlistPage() {
    const grid = document.querySelector('[data-flyandbag-wishlist-grid]');
    if (!grid) return;

    const empty = document.querySelector('[data-flyandbag-wishlist-empty]');
    const loading = document.querySelector('[data-flyandbag-wishlist-loading]');
    const countLabel = document.querySelector('[data-flyandbag-wishlist-count-label]');
    const initialItems = readItems();

    if (countLabel) {
      countLabel.textContent = `${initialItems.length} article${initialItems.length > 1 ? 's' : ''}`;
    }

    if (initialItems.length === 0) {
      grid.replaceChildren();
      grid.hidden = true;
      if (empty) empty.hidden = false;
      if (loading) loading.hidden = true;
      return;
    }

    if (loading) loading.hidden = false;
    if (empty) empty.hidden = true;

    const enrichedItems = await Promise.all(initialItems.map(enrichItem));
    writeItems(enrichedItems);

    grid.replaceChildren(...enrichedItems.map(makeWishlistCard));
    grid.hidden = false;
    if (loading) loading.hidden = true;

    if (countLabel) {
      countLabel.textContent = `${enrichedItems.length} article${enrichedItems.length > 1 ? 's' : ''}`;
    }
  }

  function scan(root = document) {
    injectCardButtons(root);
    injectProductButton();
    refreshButtons();
    updateWishlistLinks();
  }

  function queueScan(root) {
    if (scanQueued) return;
    scanQueued = true;

    window.requestAnimationFrame(() => {
      scanQueued = false;
      scan(root || document);
    });
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest(TOGGLE_SELECTOR);
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      toggleItem(toggle);
      return;
    }

    const remove = event.target.closest('[data-flyandbag-wishlist-remove]');
    if (remove) {
      event.preventDefault();
      const handle = remove.dataset.flyandbagWishlistRemove;
      writeItems(readItems().filter((item) => item.handle !== handle));
      showToast('Article retiré de votre wishlist');
      refreshButtons();
      updateWishlistLinks();
      renderWishlistPage();
    }
  });

  document.addEventListener('flyandbag:wishlist-change', () => {
    refreshButtons();
    updateWishlistLinks();
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    refreshButtons();
    updateWishlistLinks();
    renderWishlistPage();
  });

  function init() {
    readItems();
    scan();
    renderWishlistPage();

    const observer = new MutationObserver((mutations) => {
      const relevantMutation = mutations.find((mutation) =>
        Array.from(mutation.addedNodes).some((node) => node instanceof Element)
      );
      if (relevantMutation) queueScan(document);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
